import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { adminLoginStart, adminLoginSuccess, adminLoginFailure } from '../../store/adminSlice';
import { showToast } from '../../store/uiSlice';

const AdminLogin = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      dispatch(showToast({ message: 'Please fill in all fields.', type: 'error' }));
      return;
    }
    
    setIsLoading(true);
    dispatch(adminLoginStart());
    
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/admin/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        localStorage.setItem('adminToken', data.token);
        localStorage.setItem('adminUser', JSON.stringify(data.admin));
        localStorage.setItem('adminLoginTime', Date.now().toString());
        
        dispatch(adminLoginSuccess({ admin: data.admin, token: data.token }));
        dispatch(showToast({ message: 'Welcome back, Admin!', type: 'success' }));
        
        setTimeout(() => { navigate('/admin/dashboard'); }, 100);
      } else {
        dispatch(adminLoginFailure(data.message));
        dispatch(showToast({ message: data.message || 'Invalid credentials', type: 'error' }));
      }
    } catch (error) {
      console.error('Login error:', error);
      dispatch(adminLoginFailure(error.message));
      dispatch(showToast({ message: 'Login failed. Please try again.', type: 'error' }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-6">
      {/* Header Navigation */}
      <div className="absolute top-0 left-0 right-0 h-20 flex items-center justify-between px-8  border-[#e2e8f0]">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3">
          <img 
            src="/src/assets/img/logo/logo-white.svg" 
            alt="Anzo Trade" 
            className="w-[10.5rem] h-[10.5rem] object-contain"
          />
        </Link>

        {/* Back Link */}
        <a href="/" className="text-sm tracking-widest font-light text-[#64748b] hover:text-[#8b5cf6] transition-colors uppercase">
          Back
        </a>
      </div>

      {/* Main Container */}
      <div className="w-full max-w-md">
        <div className="mt-12">
          {/* Title Section */}
          <div className="mb-12">
            <h1 className="text-5xl font-light text-[#0f172a] mb-3 tracking-tight">
              Sign In
            </h1>
            <p className="text-sm tracking-widest font-light text-[#8b5cf6] uppercase">
              Admin Portal Access
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Email Input */}
            <div>
              <label className="block text-xs tracking-widest font-light text-[#5b21b6] uppercase mb-3">
                Email
              </label>
              <div className="relative">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => setFocusedField('email')}
                  onBlur={() => setFocusedField(null)}
                  placeholder="example@example.com"
                  className={`w-full px-4 py-3.5 text-sm text-[#0f172a] font-light bg-transparent border-b-2 outline-none transition-all duration-300 placeholder:text-[#cbd5e1] ${
                    focusedField === 'email'
                      ? 'border-b-[#8b5cf6]'
                      : 'border-b-[#e2e8f0] hover:border-b-[#a78bfa]'
                  }`}
                />
              </div>
            </div>

            {/* Password Input */}
            <div>
              <label className="block text-xs tracking-widest font-light text-[#5b21b6] uppercase mb-3">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setFocusedField('password')}
                  onBlur={() => setFocusedField(null)}
                  placeholder="••••••••"
                  className={`w-full px-4 py-3.5 text-sm text-[#0f172a] font-light bg-transparent border-b-2 outline-none transition-all duration-300 placeholder:text-[#cbd5e1] ${
                    focusedField === 'password'
                      ? 'border-b-[#8b5cf6]'
                      : 'border-b-[#e2e8f0] hover:border-b-[#a78bfa]'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-[#94a3b8] hover:text-[#8b5cf6] transition-colors p-2"
                >
                  <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'} text-xs`} />
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-4 px-6 mt-10 font-light text-sm uppercase tracking-widest text-white transition-all duration-300 ${
                isLoading
                  ? 'opacity-60 cursor-not-allowed bg-[#a78bfa]'
                  : 'bg-gradient-to-r from-[#7c3aed] via-[#8b5cf6] to-[#6366f1] hover:shadow-lg hover:shadow-[#8b5cf6]/25 active:scale-98'
              }`}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="inline-block w-4 h-4 border border-white border-t-transparent rounded-full animate-spin" />
                  Authenticating
                </span>
              ) : (
                'Access Dashboard'
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="mt-10 mb-6 flex items-center gap-4">
            <div className="flex-1 h-px bg-[#e2e8f0]" />
            <span className="text-xs text-[#8b5cf6] font-light">Secure Portal</span>
            <div className="flex-1 h-px bg-[#e2e8f0]" />
          </div>

          {/* Footer Info */}
          <div className="text-center">
            <p className="text-xs text-[#94a3b8] font-light tracking-widest">
              This is a secure admin area. Unauthorized access is prohibited.
            </p>
          </div>
        </div>
      </div>

      <style>{`
        input::placeholder {
          font-weight: 300;
        }
        
        button:active {
          transform: scale(0.98);
        }
      `}</style>
    </div>
  );
};

export default AdminLogin;